import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import api from '../../services/api';
import {
  Plus, RefreshCw, ExternalLink, Copy, CheckCircle2,
  AlertCircle, Link2, Unlink, ChevronDown, ChevronUp,
  FileCode2, Shield, Layers, Hash,
} from 'lucide-react';

/* ───────────────────────── types ───────────────────────── */

interface TenantContract {
  id: string;
  tenant_id: string;
  contract_address: string;
  contract_name: string;
  network: string;
  chain_id: string;
  deployer_address: string | null;
  operator_wallet: string | null;
  status: 'active' | 'paused' | 'deprecated';
  deployment_tx: string | null;
  metadata: Record<string, unknown>;
  deal_count: number;
  created_at: string;
}

interface DealAssignment {
  id: string;
  contract_id: string;
  deal_id: string;
  token_id: number | null;
  total_shares: number;
  minted_shares: number;
  reserved_shares: number;
  status: string;
  deal_title: string | null;
  deal_type: string | null;
  deal_status: string | null;
  contract_address?: string;
  network?: string;
  raise_target_cents?: number;
  raised_cents?: number;
  created_at: string;
}

interface ContractStats {
  contracts: { total: number; active: number };
  assignments: { total: number; total_shares: number; minted_shares: number; reserved_shares: number };
  reserves: Array<{
    tenant_id: string;
    asset: string;
    locked_count: number;
    total_locked_shares: number;
    total_locked_usd: number;
    total_locked_crypto: number;
    settled_count: number;
    total_settled_usd: number;
  }>;
}

/* ───────────────────────── helpers ───────────────────────── */

const NETWORKS: Record<string, { name: string; explorer: string }> = {
  sepolia:   { name: 'Sepolia',   explorer: 'https://sepolia.etherscan.io' },
  mainnet:   { name: 'Ethereum',  explorer: 'https://etherscan.io' },
  ethereum:  { name: 'Ethereum',  explorer: 'https://etherscan.io' },
  arbitrum:  { name: 'Arbitrum',  explorer: 'https://arbiscan.io' },
  polygon:   { name: 'Polygon',   explorer: 'https://polygonscan.com' },
  localhost: { name: 'Local',     explorer: '' },
  hardhat:   { name: 'Hardhat',   explorer: '' },
};

const shortAddr = (addr: string) => addr ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : '—';

const explorerLink = (network: string, address: string) => {
  const net = NETWORKS[network];
  if (!net?.explorer) return null;
  return `${net.explorer}/address/${address}`;
};

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('en-US', {
  month: 'short', day: 'numeric', year: 'numeric',
});

const fmtUSD = (cents: number) => {
  if (!cents && cents !== 0) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
};

/* ═══════════════════════════════════════════════════════════════════
   SMART CONTRACTS TAB
   ═══════════════════════════════════════════════════════════════════ */

const SmartContractsTab: React.FC = () => {
  const [contracts, setContracts] = useState<TenantContract[]>([]);
  const [stats, setStats] = useState<ContractStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Register form
  const [showRegister, setShowRegister] = useState(false);
  const [regForm, setRegForm] = useState({
    contractAddress: '',
    network: 'sepolia',
    chainId: '11155111',
    deployerAddress: '',
    operatorWallet: '',
    deploymentTx: '',
  });
  const [registering, setRegistering] = useState(false);

  // Expanded contract (shows assignments)
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [assignments, setAssignments] = useState<DealAssignment[]>([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);

  // Assign deal form
  const [showAssign, setShowAssign] = useState(false);
  const [assignForm, setAssignForm] = useState({ contractId: '', dealId: '', tokenId: '', totalShares: '' });
  const [assigning, setAssigning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, sRes] = await Promise.all([
        api.get('/admin/treasury/contracts').catch(() => null) as Promise<any>,
        api.get('/admin/treasury/contracts/stats').catch(() => null) as Promise<any>,
      ]);
      if (cRes?.data) setContracts(Array.isArray(cRes.data) ? cRes.data : []);
      if (sRes?.data) setStats(sRes.data);
    } catch (err) {
      console.error('Load contracts error', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const handleRegister = async () => {
    if (!regForm.contractAddress.trim()) { toast.error('Contract address required'); return; }
    setRegistering(true);
    try {
      await api.post('/admin/treasury/contracts', regForm);
      toast.success('Contract registered');
      setShowRegister(false);
      setRegForm({ contractAddress: '', network: 'sepolia', chainId: '11155111', deployerAddress: '', operatorWallet: '', deploymentTx: '' });
      await load();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to register contract');
    } finally {
      setRegistering(false);
    }
  };

  const toggleExpand = async (contractId: string) => {
    if (expandedId === contractId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(contractId);
    setLoadingAssignments(true);
    try {
      const res = await api.get(`/admin/treasury/contracts/${contractId}/assignments`) as any;
      setAssignments(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setAssignments([]);
    } finally {
      setLoadingAssignments(false);
    }
  };

  const handleAssignDeal = async () => {
    if (!assignForm.contractId || !assignForm.dealId) { toast.error('Contract and Deal are required'); return; }
    setAssigning(true);
    try {
      await api.post('/admin/treasury/contracts/assign', {
        contractId: assignForm.contractId,
        dealId: assignForm.dealId,
        tokenId: assignForm.tokenId ? parseInt(assignForm.tokenId) : null,
        totalShares: assignForm.totalShares ? parseInt(assignForm.totalShares) : 0,
      });
      toast.success('Deal assigned to contract');
      setShowAssign(false);
      setAssignForm({ contractId: '', dealId: '', tokenId: '', totalShares: '' });
      if (expandedId) toggleExpand(expandedId);
      await load();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to assign deal');
    } finally {
      setAssigning(false);
    }
  };

  const handleUnassign = async (assignmentId: string) => {
    if (!window.confirm('Close this deal assignment?')) return;
    try {
      await api.delete(`/admin/treasury/contracts/assign/${assignmentId}`);
      toast.success('Assignment closed');
      if (expandedId) toggleExpand(expandedId);
      await load();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to unassign');
    }
  };

  const handleStatusChange = async (contractId: string, status: string) => {
    try {
      await api.patch(`/admin/treasury/contracts/${contractId}`, { status });
      toast.success(`Contract ${status}`);
      await load();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update');
    }
  };

  const copyAddr = (addr: string) => {
    navigator.clipboard.writeText(addr);
    toast.success('Copied');
  };

  if (loading) return <div className="py-12 flex justify-center"><LoadingSpinner /></div>;

  return (
    <div className="space-y-6">
      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Active Contracts" value={stats.contracts.active} icon={<FileCode2 className="w-5 h-5 text-primary" />} />
          <StatCard label="Deal Assignments" value={stats.assignments.total} icon={<Link2 className="w-5 h-5 text-primary" />} />
          <StatCard label="Total Shares" value={Number(stats.assignments.total_shares).toLocaleString()} icon={<Layers className="w-5 h-5 text-primary" />} />
          <StatCard label="Reserved Shares" value={Number(stats.assignments.reserved_shares).toLocaleString()} icon={<Shield className="w-5 h-5 text-primary" />} />
        </div>
      )}

      {/* Reserve summary by asset */}
      {stats?.reserves && stats.reserves.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-semibold mb-3">Reserve Summary</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {stats.reserves.map((r) => (
              <div key={r.asset} className="border border-border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-lg">{r.asset}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${r.locked_count > 0 ? 'bg-yellow-500/10 text-yellow-400' : 'bg-muted text-muted-foreground'}`}>
                    {r.locked_count} locked
                  </span>
                </div>
                <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                  <p>Locked: {Number(r.total_locked_shares).toLocaleString()} shares · ${Number(r.total_locked_usd).toLocaleString()}</p>
                  <p>Settled: {r.settled_count} · ${Number(r.total_settled_usd).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-lg font-semibold">Deployed Contracts</h2>
        <div className="flex gap-2">
          <button onClick={handleRefresh} disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-muted transition">
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button onClick={() => { setShowAssign(true); setAssignForm({ ...assignForm, contractId: contracts[0]?.id || '' }); }}
            disabled={contracts.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-muted transition">
            <Link2 className="w-3.5 h-3.5" /> Assign Deal
          </button>
          <button onClick={() => setShowRegister(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition">
            <Plus className="w-3.5 h-3.5" /> Register Contract
          </button>
        </div>
      </div>

      {/* Register form */}
      {showRegister && (
        <div className="bg-card border border-primary/30 rounded-xl p-5 space-y-4">
          <h3 className="text-sm font-semibold">Register Deployed Contract</h3>
          <p className="text-xs text-muted-foreground">
            Deploy via <code className="bg-muted px-1 rounded">npx hardhat run scripts/deployTenant.js --network sepolia</code>, then register here.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input placeholder="Contract Address (0x…)" value={regForm.contractAddress}
              onChange={(e) => setRegForm({ ...regForm, contractAddress: e.target.value })}
              className="px-3 py-2 text-sm border border-border rounded-lg bg-background" />
            <select value={regForm.network} onChange={(e) => setRegForm({ ...regForm, network: e.target.value })}
              className="px-3 py-2 text-sm border border-border rounded-lg bg-background">
              <option value="sepolia">Sepolia</option>
              <option value="mainnet">Ethereum Mainnet</option>
              <option value="arbitrum">Arbitrum</option>
              <option value="polygon">Polygon</option>
              <option value="localhost">Localhost</option>
            </select>
            <input placeholder="Deployer Address" value={regForm.deployerAddress}
              onChange={(e) => setRegForm({ ...regForm, deployerAddress: e.target.value })}
              className="px-3 py-2 text-sm border border-border rounded-lg bg-background" />
            <input placeholder="Operator Wallet (treasury)" value={regForm.operatorWallet}
              onChange={(e) => setRegForm({ ...regForm, operatorWallet: e.target.value })}
              className="px-3 py-2 text-sm border border-border rounded-lg bg-background" />
            <input placeholder="Deployment Tx Hash" value={regForm.deploymentTx}
              onChange={(e) => setRegForm({ ...regForm, deploymentTx: e.target.value })}
              className="px-3 py-2 text-sm border border-border rounded-lg bg-background col-span-full" />
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowRegister(false)}
              className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-muted">Cancel</button>
            <button onClick={handleRegister} disabled={registering}
              className="px-4 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50">
              {registering ? 'Registering…' : 'Register'}
            </button>
          </div>
        </div>
      )}

      {/* Assign deal form */}
      {showAssign && (
        <div className="bg-card border border-primary/30 rounded-xl p-5 space-y-4">
          <h3 className="text-sm font-semibold">Assign Deal to Contract</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <select value={assignForm.contractId}
              onChange={(e) => setAssignForm({ ...assignForm, contractId: e.target.value })}
              className="px-3 py-2 text-sm border border-border rounded-lg bg-background">
              <option value="">Select contract…</option>
              {contracts.filter(c => c.status === 'active').map((c) => (
                <option key={c.id} value={c.id}>{shortAddr(c.contract_address)} ({NETWORKS[c.network]?.name || c.network})</option>
              ))}
            </select>
            <input placeholder="Deal ID (UUID)" value={assignForm.dealId}
              onChange={(e) => setAssignForm({ ...assignForm, dealId: e.target.value })}
              className="px-3 py-2 text-sm border border-border rounded-lg bg-background" />
            <input placeholder="On-chain Token ID (optional)" value={assignForm.tokenId}
              onChange={(e) => setAssignForm({ ...assignForm, tokenId: e.target.value })}
              className="px-3 py-2 text-sm border border-border rounded-lg bg-background" />
            <input placeholder="Total Shares" value={assignForm.totalShares}
              onChange={(e) => setAssignForm({ ...assignForm, totalShares: e.target.value })}
              className="px-3 py-2 text-sm border border-border rounded-lg bg-background" />
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowAssign(false)}
              className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-muted">Cancel</button>
            <button onClick={handleAssignDeal} disabled={assigning}
              className="px-4 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50">
              {assigning ? 'Assigning…' : 'Assign'}
            </button>
          </div>
        </div>
      )}

      {/* Contract list */}
      {contracts.length === 0 ? (
        <div className="bg-card border border-dashed border-border rounded-xl p-12 text-center">
          <FileCode2 className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <h3 className="font-semibold mb-1">No contracts deployed yet</h3>
          <p className="text-sm text-muted-foreground">
            Deploy a TenantPropertyToken contract and register it here to start minting deals.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {contracts.map((c) => (
            <div key={c.id} className="bg-card border border-border rounded-xl overflow-hidden">
              {/* Contract header row */}
              <div className="p-4 flex items-center justify-between gap-4 cursor-pointer hover:bg-muted/30 transition"
                onClick={() => toggleExpand(c.id)}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${c.status === 'active' ? 'bg-green-500' : c.status === 'paused' ? 'bg-yellow-500' : 'bg-gray-500'}`} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm font-semibold">{shortAddr(c.contract_address)}</span>
                      <button onClick={(e) => { e.stopPropagation(); copyAddr(c.contract_address); }}
                        className="text-muted-foreground hover:text-foreground"><Copy className="w-3.5 h-3.5" /></button>
                      {explorerLink(c.network, c.contract_address) && (
                        <a href={explorerLink(c.network, c.contract_address)!} target="_blank" rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-muted-foreground hover:text-primary"><ExternalLink className="w-3.5 h-3.5" /></a>
                      )}
                      <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                        {NETWORKS[c.network]?.name || c.network}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {c.deal_count} deal{c.deal_count !== 1 ? 's' : ''} assigned · Deployed {fmtDate(c.created_at)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <select value={c.status}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => handleStatusChange(c.id, e.target.value)}
                    className="text-xs px-2 py-1 border border-border rounded bg-background">
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="deprecated">Deprecated</option>
                  </select>
                  {expandedId === c.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
              </div>

              {/* Expanded: deal assignments */}
              {expandedId === c.id && (
                <div className="border-t border-border bg-muted/20 p-4">
                  {loadingAssignments ? (
                    <div className="py-4 flex justify-center"><LoadingSpinner /></div>
                  ) : assignments.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-3">No deals assigned to this contract yet.</p>
                  ) : (
                    <div className="space-y-2">
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Deal Assignments</h4>
                      {assignments.map((a) => (
                        <div key={a.id} className="flex items-center justify-between p-3 bg-background rounded-lg border border-border">
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{a.deal_title || a.deal_id}</p>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                              {a.token_id !== null && <span className="flex items-center gap-1"><Hash className="w-3 h-3" />Token {a.token_id}</span>}
                              <span>{Number(a.total_shares).toLocaleString()} shares</span>
                              {a.reserved_shares > 0 && <span className="text-yellow-500">{Number(a.reserved_shares).toLocaleString()} reserved</span>}
                              <span className={`px-1.5 py-0.5 rounded ${a.status === 'active' ? 'bg-green-500/10 text-green-400' : 'bg-muted text-muted-foreground'}`}>
                                {a.status}
                              </span>
                            </div>
                          </div>
                          <button onClick={() => handleUnassign(a.id)}
                            className="text-muted-foreground hover:text-destructive text-xs flex items-center gap-1 px-2 py-1 rounded hover:bg-destructive/10">
                            <Unlink className="w-3 h-3" /> Close
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


/* ═══════════════════════════════════════════════════════════════════
   STAT CARD HELPER
   ═══════════════════════════════════════════════════════════════════ */

const StatCard: React.FC<{ label: string; value: string | number; icon: React.ReactNode }> = ({ label, value, icon }) => (
  <div className="bg-card border border-border rounded-xl p-4">
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium text-muted-foreground">{label}</span>
      {icon}
    </div>
    <p className="text-2xl font-bold mt-1">{value}</p>
  </div>
);

export { SmartContractsTab };
export default SmartContractsTab;
